import React, { useEffect, useState } from 'react';
import { storage } from '../services/api';

interface StorageData {
  recording_storage?: {
    limit_gb: number;
    used_gb: number;
    available_gb: number;
    usage_percent: number;
    recording_count: number;
  };
  cloud_storage?: {
    limit_gb: number;
    used_gb: number;
    available_gb: number;
    usage_percent: number;
    recording_count: number;
  };
  total_disk?: {
    total_gb: number;
    available_gb: number;
  };
}

interface StorageWidgetProps {
  deviceId?: string;
  venueId?: string;
  type: 'device' | 'venue';
  refreshInterval?: number;
  onLimitUpdate?: () => void;
}

const StorageWidget: React.FC<StorageWidgetProps> = ({
  deviceId,
  venueId,
  type,
  refreshInterval = 30000,
  onLimitUpdate
}) => {
  const [storageData, setStorageData] = useState<StorageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingLimit, setEditingLimit] = useState(false);
  const [newLimit, setNewLimit] = useState('');

  const fetchStorage = async () => {
    if (!deviceId && !venueId) return;

    try {
      const response = type === 'device'
        ? await storage.getDeviceStorage(deviceId!)
        : await storage.getVenueStorage(venueId!);
      setStorageData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch storage:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStorage();
    const interval = setInterval(fetchStorage, refreshInterval);
    return () => clearInterval(interval);
  }, [deviceId, venueId, type, refreshInterval]);

  const handleUpdateLimit = async () => {
    if (!newLimit || isNaN(parseFloat(newLimit))) return;
    if (!deviceId && !venueId) return;

    try {
      if (type === 'device') {
        await storage.updateDeviceLimit(deviceId!, parseFloat(newLimit));
      } else {
        await storage.updateVenueLimit(venueId!, parseFloat(newLimit));
      }
      setEditingLimit(false);
      setNewLimit('');
      fetchStorage();
      if (onLimitUpdate) onLimitUpdate();
    } catch (error) {
      console.error('Failed to update limit:', error);
      alert('Failed to update storage limit');
    }
  };

  if (loading || !storageData) {
    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="text-gray-500 dark:text-gray-400">Loading storage info...</div>
      </div>
    );
  }

  const getUsageColor = (percent: number) => {
    if (percent >= 90) return 'bg-red-500';
    if (percent >= 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTextColor = (percent: number) => {
    if (percent >= 90) return 'text-red-600 dark:text-red-400';
    if (percent >= 75) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-green-600 dark:text-green-400';
  };

  const StorageBar: React.FC<{ label: string; percent: number; used: number; total: number }> = ({ label, percent, used, total }) => (
    <div className="mb-4">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <span className={`text-lg font-semibold ${getTextColor(percent)}`}>
          {percent.toFixed(1)}%
        </span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
        <div
          className={`h-full ${getUsageColor(percent)} transition-all duration-300`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-600 dark:text-gray-400">
        <span>{used.toFixed(2)} GB used</span>
        <span>{total.toFixed(2)} GB total</span>
      </div>
    </div>
  );

  // For device view, show all three layers
  if (type === 'device' && storageData.total_disk && storageData.recording_storage) {
    const physicalDisk = storageData.total_disk;
    const recordingQuota = storageData.recording_storage;

    const physicalUsedGB = physicalDisk.total_gb - physicalDisk.available_gb;
    const physicalUsagePercent = (physicalUsedGB / physicalDisk.total_gb) * 100;

    return (
      <div className="space-y-4">
        {/* Physical Disk Card */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              📦 Physical Device Disk
            </h3>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Total Capacity
            </span>
          </div>

          <StorageBar
            label="Disk Usage (OS + Recordings + System)"
            percent={physicalUsagePercent}
            used={physicalUsedGB}
            total={physicalDisk.total_gb}
          />

          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="text-gray-600 dark:text-gray-400">Total</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {physicalDisk.total_gb.toFixed(2)} GB
              </div>
            </div>
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="text-gray-600 dark:text-gray-400">Used</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {physicalUsedGB.toFixed(2)} GB
              </div>
            </div>
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
              <div className="text-gray-600 dark:text-gray-400">Free</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {physicalDisk.available_gb.toFixed(2)} GB
              </div>
            </div>
          </div>
        </div>

        {/* Recording Quota Card */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                🎵 Local Recording Quota
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Allocated space within physical disk
              </p>
            </div>
            <button
              onClick={() => setEditingLimit(!editingLimit)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              {editingLimit ? 'Cancel' : 'Edit Limit'}
            </button>
          </div>

          {editingLimit && (
            <div className="mb-4 flex gap-2">
              <input
                type="number"
                value={newLimit}
                onChange={(e) => setNewLimit(e.target.value)}
                placeholder={`${recordingQuota.limit_gb} GB`}
                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                         bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
              />
              <button
                onClick={handleUpdateLimit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
              >
                Save
              </button>
            </div>
          )}

          <StorageBar
            label="Recording Storage"
            percent={recordingQuota.usage_percent}
            used={recordingQuota.used_gb}
            total={recordingQuota.limit_gb}
          />

          <div className="grid grid-cols-3 gap-3 text-xs">
            <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
              <div className="text-gray-600 dark:text-gray-400">Limit</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {recordingQuota.limit_gb.toFixed(2)} GB
              </div>
            </div>
            <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
              <div className="text-gray-600 dark:text-gray-400">Used</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {recordingQuota.used_gb.toFixed(2)} GB
              </div>
            </div>
            <div className="text-center p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
              <div className="text-gray-600 dark:text-gray-400">Available</div>
              <div className="font-semibold text-gray-900 dark:text-gray-100">
                {recordingQuota.available_gb.toFixed(2)} GB
              </div>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <span className="text-xs text-gray-600 dark:text-gray-400">Local Recordings</span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {recordingQuota.recording_count} files
            </span>
          </div>

          {recordingQuota.usage_percent >= 90 && (
            <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-800 dark:text-red-200">
              ⚠️ Recording quota critically low! Old files will be auto-deleted.
            </div>
          )}
          {recordingQuota.usage_percent >= 75 && recordingQuota.usage_percent < 90 && (
            <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-800 dark:text-yellow-200">
              Recording quota approaching limit.
            </div>
          )}
        </div>

        {/* Cloud Storage Info Note */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 dark:text-blue-400">ℹ️</span>
            <div className="text-xs text-blue-800 dark:text-blue-200">
              <strong>Storage Flow:</strong> Recordings are temporarily stored locally (quota above),
              then automatically uploaded to cloud storage and deleted from device to free space.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // For venue view, show cloud storage only
  if (type === 'venue' && storageData.cloud_storage) {
    const cloudStorage = storageData.cloud_storage;

    return (
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              ☁️ Cloud Storage
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Permanent storage for all uploaded recordings
            </p>
          </div>
          <button
            onClick={() => setEditingLimit(!editingLimit)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            {editingLimit ? 'Cancel' : 'Edit Limit'}
          </button>
        </div>

        {editingLimit && (
          <div className="mb-4 flex gap-2">
            <input
              type="number"
              value={newLimit}
              onChange={(e) => setNewLimit(e.target.value)}
              placeholder={`${cloudStorage.limit_gb} GB`}
              className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md
                       bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
            />
            <button
              onClick={handleUpdateLimit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Save
            </button>
          </div>
        )}

        <StorageBar
          label="Cloud Usage"
          percent={cloudStorage.usage_percent}
          used={cloudStorage.used_gb}
          total={cloudStorage.limit_gb}
        />

        <div className="grid grid-cols-3 gap-3 text-xs">
          <div className="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
            <div className="text-gray-600 dark:text-gray-400">Limit</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {cloudStorage.limit_gb.toFixed(0)} GB
            </div>
          </div>
          <div className="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
            <div className="text-gray-600 dark:text-gray-400">Used</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {cloudStorage.used_gb.toFixed(2)} GB
            </div>
          </div>
          <div className="text-center p-2 bg-purple-50 dark:bg-purple-900/20 rounded">
            <div className="text-gray-600 dark:text-gray-400">Available</div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {cloudStorage.available_gb.toFixed(2)} GB
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <span className="text-xs text-gray-600 dark:text-gray-400">Uploaded Recordings</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {cloudStorage.recording_count} files
          </span>
        </div>

        {cloudStorage.usage_percent >= 90 && (
          <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-800 dark:text-red-200">
            ⚠️ Cloud storage critically low! Consider increasing limit or deleting old recordings.
          </div>
        )}
        {cloudStorage.usage_percent >= 75 && cloudStorage.usage_percent < 90 && (
          <div className="mt-3 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded text-xs text-yellow-800 dark:text-yellow-200">
            Cloud storage approaching limit.
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default StorageWidget;
